import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function HeroSection({ onBookingClick }) {
  const fadeUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const trustBadges = [
    "Værdiforøgelse garanti",
    "Hurtigere salg",
    "Professionel rådgivning"
  ];

  return (
    <section className="relative min-h-screen pt-20" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-24">
        {/* Tetris Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          {/* Left Content - 7 columns */}
          <div className="lg:col-span-7 space-y-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 bg-secondary px-4 py-2 rounded-none"
            >
              <span className="text-xs font-semibold uppercase tracking-widest text-primary">
                Salgsrenovering specialist
              </span>
            </motion.div>

            <motion.h1
              {...fadeUp}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-5xl md:text-6xl lg:text-7xl font-bold text-foreground tracking-tight leading-[1.1]"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Maksimal værdi
              <br />
              <span className="text-primary">for din bolig</span>
            </motion.h1>

            <motion.p
              {...fadeUp}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-lg md:text-xl text-muted-foreground max-w-xl leading-relaxed"
            >
              Vi renoverer din bolig strategisk med henblik på et hurtigere salg 
              samt en markant større salgspris. Professionel rådgivning fra første kontakt.
            </motion.p>

            {/* Trust Badges */}
            <motion.div
              {...fadeUp}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-wrap gap-4"
            >
              {trustBadges.map((badge, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-2 text-sm text-muted-foreground"
                >
                  <CheckCircle2 className="w-4 h-4 text-accent" />
                  <span>{badge}</span>
                </div>
              ))}
            </motion.div>

            {/* CTA Buttons */}
            <motion.div
              {...fadeUp}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-4 pt-4"
            >
              <Button
                onClick={onBookingClick}
                size="lg"
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none px-8 py-6 text-base group"
                data-testid="hero-booking-btn"
              >
                Book Gratis Konsultation
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-none px-8 py-6 text-base"
                onClick={() => document.getElementById('projekter').scrollIntoView({ behavior: 'smooth' })}
                data-testid="hero-projects-btn"
              >
                Se Vores Projekter
              </Button>
            </motion.div>
          </div>

          {/* Right Content - Image Collage - 5 columns */}
          <div className="lg:col-span-5 hidden lg:block">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="grid grid-cols-2 gap-4"
            >
              {/* Main large image */}
              <div className="col-span-2 aspect-[4/3] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80"
                  alt="Moderne renoveret stue"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Two smaller images */}
              <div className="aspect-square overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=400&q=80"
                  alt="Renoveret køkken"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="aspect-square overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=400&q=80"
                  alt="Moderne badeværelse"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>

        {/* Stats Bar */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-20 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 py-12 border-t border-b border-border"
        >
          {[
            { value: "20-40%", label: "Værdiforøgelse" },
            { value: "14-30", label: "Dages byggeperiode" },
            { value: "✓", label: "Hurtig respons" },
            { value: "✓", label: "Samarbejde med mægler" },
            { value: "✓", label: "Stor økonomisk værdiforøgelse for lille investering" },
            { value: "✓", label: "Gratis konsultation på adressen" },
          ].map((stat, index) => (
            <div key={index} className="text-center">
              <div className="text-2xl md:text-3xl font-bold text-primary" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {stat.value}
              </div>
              <div className="mt-2 text-xs text-muted-foreground uppercase tracking-wide">
                {stat.label}
              </div>
            </div>
          ))}
        </motion.div>

        {/* Financing Highlight */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-8 bg-primary text-primary-foreground p-6 md:p-8 text-center"
        >
          <div className="max-w-2xl mx-auto">
            <h3 className="text-xl md:text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
              Fuld finansiering tilbydes
            </h3>
            <p className="mt-2 text-primary-foreground/80 text-sm md:text-base">
              Indfries først ved et salg — ingen udgifter før din bolig er solgt
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
